import {
  type Board,
  type BoardType,
  type Coupon,
  type CouponId,
  type CouponType,
  getCouponId,
  type OutcomeGroup,
  type Reduction,
  type ReductionDistributionOutcome,
} from '../../../../../schema/coupon/entry.js';
import type { DrawNumber } from '../../../../../schema/draw/entry.js';
import type { Definition } from '../../../../../schema/frameworks/entry.js';
import type { SystemType } from '../../../../../schema/system/entry.js';
import { type IDefinition, NotImplementedError } from '../../../../domain/entry.js';
import { NO_VALUE_SET } from '../../../../lib/entry.browser.js';

const createNewOutcomeGroup = (): OutcomeGroup => [];

type CouponBoard = Omit<Board, 'type' | 'quickPickSize' | 'quickPickVariant'>;

type CouponConfig = {
  product: Definition['drawProducts'][number];
  drawNumber: DrawNumber;
  eventCount: number;
};

/**
 * Creates coupon board to fill outcome bet information.
 *
 * @param config Draw object
 * @param definition Product definitionEntity
 * @returns Score board entity
 */
const createCouponBoard = (config: CouponConfig, definition: IDefinition): CouponBoard => {
  const reductionValue = definition.reductionSum;

  const reduction: Reduction[] = Array.from<Reduction>({ length: config.eventCount }).fill(reductionValue);

  const outcomeIt = Array.from<unknown>({ length: definition.outcomeGroupCount });
  const eventIt = Array.from<unknown>({ length: config.eventCount });

  return {
    outcomes: eventIt.map(() => outcomeIt.map(() => createNewOutcomeGroup())),
    userOutcomes: eventIt.map(() => outcomeIt.map(() => createNewOutcomeGroup())),
    outcomeDistribution: eventIt.map(() =>
      outcomeIt.map(() =>
        Array.from<ReductionDistributionOutcome>({ length: definition.outcomeCount }).fill(
          0 as ReductionDistributionOutcome
        )
      )
    ),
    reduction,
    uOutcomes: Array.from<number | null>({ length: config.eventCount }).fill(NO_VALUE_SET),
    mOutcomes: Array.from<boolean>({ length: config.eventCount }).fill(false),
  };
};

/**
 * Creates a container board for the coupon.
 *
 * @param config Draw object
 * @param type Board type which describes the board
 * @param definition Product definitionEntity
 * @returns Board entity
 */
const createBoard = (config: CouponConfig, type: BoardType, definition: IDefinition): Board => {
  const board = createCouponBoard(config, definition);

  return {
    type,
    quickPickSize: definition.model.defaultCouponValues.quickPickSize,
    quickPickVariant: undefined,
    ...board,
  };
};

type ExcludedSystem = Exclude<SystemType, 'SROW' | 'MSYS'>;

const findFirstSystem = (systems: readonly SystemType[]): ExcludedSystem | undefined => {
  for (const sys of systems) {
    if (!['SROW', 'MSYS'].includes(sys)) {
      return sys as ExcludedSystem;
    }
  }
  return undefined;
};

const createCoupon = ({
  config,
  definition,
  couponType,
  betAmount,
}: {
  config: CouponConfig;
  definition: IDefinition;
  couponType: CouponType;
  betAmount?: number;
}): Coupon => {
  return {
    product: config.product,
    drawNumber: config.drawNumber,
    // engineType, // Used when we can't access whole state etc state-slices. For example reducers
    boards: [createBoard(config, 'BOARD_MULTIPLE', definition)],
    betAmount: betAmount ?? definition.model.defaultCouponValues.betAmount,
    sportkryss: 0,
    sportkryssNumber: '1234567',
    week: 1,
    uSystem: undefined,
    rSystem: undefined,
    selectedSystem: 'MSYS',
    // Always set lastSelectedSystemType to be RSYS or undefined if product does not support RSYS,,
    lastSelectedSystemType: findFirstSystem(definition.systems),
    reduceModeCount: definition.reduceModeCount,
    couponType: couponType,
  } satisfies Coupon;
};

/**
 * Creates a standard coupon for MSYS, RSYS, USYS and REDUCERA_FRITT.
 *
 * @param root0 object containing draw and definition used to create the standard coupon
 * @param root0.config Draw object to create the coupon entity from
 * @param root0.definition Product definition
 * @returns Standard coupon instance
 */
function createStandardCoupon({ config, definition }: { config: CouponConfig; definition: IDefinition }): Coupon {
  // const eventType = engineDefinition.outcomes.eventType;
  // const engineType =
  //   eventTypeId === EventTypeId.RESULT ? engineCouponSelectorType.BINARY : engineCouponSelectorType.MATRIX;

  return createCoupon({ config, definition, couponType: 'standard' });
}

function createSingleRowsCoupon({
  config,
  definition,
  betAmount,
}: {
  config: CouponConfig;
  definition: IDefinition;
  betAmount?: number;
}): Coupon {
  return createCoupon({ config, definition, couponType: 'singlerows', betAmount });
}

// const createExternalCoupon = ({ draw, engineDefinition }) => ({
//   ...createSingleRowsCoupon({ draw, engineDefinition }),
//   isExternal: true,
//   boards: [],
// });

// const createPixCoupon = ({ draw, engineDefinition }) => {
//   const eventTypeId = engineDefinition.outcomes.eventTypeId;
//   const engineType =
//     eventTypeId === EventTypeId.RESULT ? engineCouponSelectorType.BINARY : engineCouponSelectorType.MATRIX;
//   return {
//     productId: draw.productId,
//     drawNumber: draw.drawNumber,
//     engineType, // Used when we can't access whole state etc state-slices. For example reducers
//     boards: [createBoard(draw, BOARD_MULTIPLE, engineDefinition)],
//     isModified: false,
//     betAmount: 1,
//     sportkryss: 0,
//     sportkryssNumber: generateSportkryssNumber(),
//     week: 0,
//     uSystem: null,
//     rSystem: null,
//     selectedSystem: engineDefinition.systems.indexOf(SYSTEM_MSYS),
//     reduceModeCount: undefined,
//     selectedAddonPixIndex: 0,
//   };
// };

// type CouponConfig = {
//   product: Product;
//   drawNumber: number;
//   eventCount: number;
// };

// type CreateCouponArg = {
//   configModel: CouponConfig;
//   definition: Definition;
//   couponTypes: CouponType[];
// };

// /**
//  * Creates the different types of coupons within tipsen.
//  * - Standard coupon.
//  * - Single rows coupon.
//  * - External coupon.
//  *
//  * @param root0 object containing draws and definition used to create the coupons
//  * @param root0.configModel Array of draws to create the coupons from
//  * @param root0.definition Product definition
//  * @param root0.couponTypes Coupon types to create coupon
//  * @returns Object containing all the coupons
//  */
// export function createCouponModel({ configModel, definition, couponTypes }: CreateCouponArg): Record<CouponId, Coupon> {
//   const coupons: Record<CouponId, Coupon> = {};

//   const drawId = getDrawId(configModel.product, configModel.drawNumber);
//   for (const type of couponTypes) {
//     switch (type) {
//       case 'standard':
//         coupons[`${drawId}_standard`] = [createStandardCoupon({ config, definition })];
//     }
//     // coupons[`${drawId}_${COUPON_SINGLEROWS}`] = createSingleRowsCoupon({ draw, engineDefinition });
//     // coupons[`${drawId}_${COUPON_EXTERNAL_SYSTEM}`] = createExternalCoupon({ draw, engineDefinition });
//   }

//   return coupons;
// }

type CreateCouponModelArg = {
  configModel: CouponConfig;
  definition: IDefinition;
  couponType: CouponType;
  betAmount?: number;
};

/**
 * Creates the different types of coupons within tipsen.
 * - Standard coupon.
 * - Single rows coupon.
 * - External coupon.
 *
 * @param root0 object containing draws and definition used to create the coupons
 * @param root0.configModel Array of draws to create the coupons from
 * @param root0.definition Product definitionEntity
 * @param root0.couponType Coupon types to create coupon
 * @param root0.betAmount Bet amount to set on the coupon, if not provided it will default to the value set in the definition
 * @returns Object containing all the coupons
 * @throws {NotImplementedError} Not implemented error
 */
export function createCouponModel({ configModel, definition, couponType, betAmount }: CreateCouponModelArg): {
  couponId: CouponId;
  model: Coupon;
} {
  const couponId = getCouponId(configModel.product, configModel.drawNumber, couponType);
  let coupon: Coupon | undefined;

  switch (couponType) {
    case 'standard':
      coupon = createStandardCoupon({ config: configModel, definition });
      break;
    case 'singlerows':
      coupon = createSingleRowsCoupon({ config: configModel, definition, betAmount });
      break;
    default:
      throw new NotImplementedError(`Coupon type not implemented: ${couponType}`);
  }
  // coupons[`${drawId}_${COUPON_SINGLEROWS}`] = createSingleRowsCoupon({ draw, engineDefinition });
  // coupons[`${drawId}_${COUPON_EXTERNAL_SYSTEM}`] = createExternalCoupon({ draw, engineDefinition });

  return { couponId, model: coupon };
}
